import { useCallback, useEffect, useRef } from 'react';
import { useFilterSetsSessionStore } from 'hooks/state/usePersistedStore';
import { useUserFilterSetService } from 'services/rest-api.service';
import { AdvancedFilterData } from 'components/filter/advancedFilter/FilterFormController';
import { UserFilterSet } from 'models/filterSet';
import { convertApiFilterSetToUsable } from 'components/filter/advancedFilter/hooks/useFilterSetsStore';
import { FilterSetMatcher } from 'utils/filterSetMatcher';

export function useFilterSet() {
  const userFilterSetService = useUserFilterSetService();
  const hasLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);

  // Get filter sets from session store
  const {
    filterSets,
    setFilterSets,
    addFilterSet,
    updateFilterSet,
    removeFilterSet,
    getFilterSet,
    validateFilterSet,
    selectedFilterSet,
    setSelectedFilterSet,
  } = useFilterSetsSessionStore();

  // Store service ref to avoid dependency issues
  const serviceRef = useRef(userFilterSetService);
  serviceRef.current = userFilterSetService;

  const loadFilterSets = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;

    try {
      const { data, error } = await serviceRef.current.getSets();
      if (!data || error) {
        console.error('Error loading filter sets:', error);
        setFilterSets([]);
      } else {
        const convertedSets = data.map(convertApiFilterSetToUsable);
        setFilterSets(convertedSets);
      }
      hasLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading filter sets:', error);
      setFilterSets([]);
    } finally {
      isLoadingRef.current = false;
    }
  }, [setFilterSets]);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadFilterSets();
    }
  }, [loadFilterSets]);

  const saveFilterSet = async (
    name: string,
    filterValues: AdvancedFilterData
  ): Promise<{ success: boolean; error?: string; filterSet?: UserFilterSet }> => {
    // Validate using store function
    const validation = validateFilterSet(name, filterValues);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    // Extract only changed filter values
    const changedFilters = FilterSetMatcher.extractChangedFilters(filterValues);

    const newUserFilterSet: UserFilterSet = {
      filterSetId: '',
      name,
      content: changedFilters,
      version: 1,
    };

    try {
      const { data, error } = await serviceRef.current.saveSet(newUserFilterSet);
      if (!data || error) {
        return { success: false, error: 'advanced-filter-error-save-failed' };
      }

      const newFilterSet = convertApiFilterSetToUsable(data);
      addFilterSet(newFilterSet);

      return { success: true, filterSet: newFilterSet };
    } catch (error) {
      console.error('💥 Error saving filter set:', error);
      return { success: false, error: 'advanced-filter-error-save-failed' };
    }
  };

  const updateFilterSetById = async (
    id: string,
    name: string,
    filterValues: AdvancedFilterData
  ): Promise<{ success: boolean; error?: string; filterSet?: UserFilterSet }> => {
    const existingSet = getFilterSet(id);
    if (!existingSet) {
      return { success: false, error: 'advanced-filter-error-set-not-found' };
    }

    // Validate using store function
    const validation = validateFilterSet(name, filterValues, id);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    // Extract only changed filter values
    const changedFilters = FilterSetMatcher.extractChangedFilters(filterValues);

    const updatedUserFilterSet: UserFilterSet = {
      filterSetId: id,
      name,
      content: changedFilters,
      version: 1,
    };

    try {
      const { data, error } = await serviceRef.current.updateSet(updatedUserFilterSet);
      if (!data || error) {
        return { success: false, error: 'advanced-filter-error-update-failed' };
      }

      const updatedSet = convertApiFilterSetToUsable(data);
      updateFilterSet(id, updatedSet);

      return { success: true, filterSet: updatedSet };
    } catch (error) {
      console.error('Error updating filter set:', error);
      return { success: false, error: 'advanced-filter-error-update-failed' };
    }
  };

  const deleteFilterSet = async (id: string): Promise<boolean> => {
    try {
      const { error } = await serviceRef.current.deleteSet(id);
      if (error) {
        console.error('Error deleting filter set:', error);
        return false;
      }

      removeFilterSet(id);
      if (selectedFilterSet === id) {
        setSelectedFilterSet(null);
      }
      return true;
    } catch (error) {
      console.error('Error deleting filter set:', error);
      return false;
    }
  };

  const applyFilterSet = (filterSetId: string, currentValues: AdvancedFilterData): AdvancedFilterData | null => {
    const filterSet = getFilterSet(filterSetId);
    if (!filterSet) return null;

    // Merge the saved partial values with current values
    return FilterSetMatcher.mergeWithCurrentFilters(currentValues, filterSet.content);
  };

  return {
    filterSets,
    saveFilterSet,
    updateFilterSet: updateFilterSetById,
    deleteFilterSet,
    getFilterSet,
    applyFilterSet,
    loadFilterSets, // Expose for manual refresh if needed
  };
}
